import React, { useMemo } from 'react'
import { Box, Stack, Typography, TableContainer} from '@mui/material'
import { useTranslation } from 'react-i18next'

import TableView, { useTable, IColumn } from 'packages/ui/Table'
import TableSkeleton from 'components/global/CUK/Skeletons/TableSkeleton'

import { useServicesSort } from '../../../store'
import { filterDuplicateServices } from '../../../helpers'
import COLUMNS_CONFIG from './columns-config'
import NoContent from '../NoContent'
import { Service } from '../../../types'

interface TableProps {
  loading: boolean
  services: Service[]
}

const Table: React.FC<TableProps> = ({loading, services}) => {
  const {t} = useTranslation()
  const [sort, setSort] = useServicesSort()
  
  const filteredServices = useMemo(() => {
    return filterDuplicateServices(services || [])
  }, [services])
  
  const sortedServices = useMemo(() => {
    return [...filteredServices].sort((a, b) => {
      let aValue, bValue

      if (sort.by === 'mes_at') {
        aValue = a?.commissioned_at ? new Date(a.commissioned_at).getTime() : 0
        bValue = b?.commissioned_at ? new Date(b.commissioned_at).getTime() : 0
      } else {
        aValue = a?.[sort.by as keyof Service] || 0
        bValue = b?.[sort.by as keyof Service] || 0
      }

      return sort.direction === 'asc' ? 
        (aValue as number) - (bValue as number) : 
        (bValue as number) - (aValue as number)
    })
  }, [filteredServices, sort])
  
  const apiTable = useTable({
    rowKey: 'id',
    loading: false,
    loadingNext: false,
    items: sortedServices,
    columns: COLUMNS_CONFIG
  })

  if (loading) {
    return (
      <>
        <Typography component={'span'} sx={{fontSize: 14, fontWeight: 600}}>{t('projects.services.title')}</Typography>
        <Box sx={{height: 200}}>
          <TableSkeleton />
        </Box>
      </>
    )
  }

  return (
    <>
      <Typography component={'span'} sx={{fontSize: 14, fontWeight: 600}}>{t('projects.services.title')}</Typography>
      {
        sortedServices.length
          ? (
            <Stack>
              <TableContainer>
                <TableView
                  apiTable={apiTable}
                  sort={{
                    ...sort,
                    direction: sort.direction as string,
                    by: sort.by as string,
                    onChange: name => {
                      const column = COLUMNS_CONFIG.find(column => column.name === name) as IColumn
                      setSort(column?.sortBy || column?.name)
                    }
                  }}
                />
              </TableContainer>
            </Stack>
          )
          : <NoContent label={t('projects.services.no_content')} />
      }
    </>
  )
}

export default Table